import React, { useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import ResponsiveNavbar, { DESKTOP_DRAWER_WIDTH, MOBILE_BOTTOM_NAV_HEIGHT } from "app/components/layout/ResponsiveNavbar";
import Footer from "app/components/layout/Footer";
import OfflineBanner from "shared/ui/OfflineBanner";
import FirstUseOnboarding from "shared/onboarding/FirstUseOnboarding";
import { MarketQuotesProvider } from "features/market/context/MarketQuotesContext";
import { Toaster } from "sonner";
import DesktopSecondaryPanel from "app/components/layout/DesktopSecondaryPanel";
import { useSecondaryPanelPreference } from "shared/hooks/useSecondaryPanelPreference";
import { Box, Button, CssBaseline, Tooltip, useMediaQuery } from "@mui/material";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import { useTranslation } from "shared/hooks/useTranslation";
import { useBreakpoint } from "shared/hooks/useBreakpoint";
import { UX_MOTION, motionMs } from "lib/uxTokens";
import { useReducedMotionPreference } from "shared/hooks/useReducedMotionPreference";
import { useMobileEffects } from "shared/mobile/hooks/useMobileEffects";
import { preloadRouteForHref } from "app/routeModules";

const DESKTOP_CONTENT_HEIGHT = "calc(100vh - 32px)";
const MOBILE_CONTENT_BOTTOM_OFFSET = `calc(${MOBILE_BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))`;

function MainLayout() {
    const { t } = useTranslation();
    const location = useLocation();
    useMobileEffects();
    const { isDesktop } = useBreakpoint();
    const isPortrait = useMediaQuery("(orientation: portrait)");
    const isVerticalDesktop = isDesktop && isPortrait;
    const prefersReducedMotion = useReducedMotionPreference();
    const transitionDuration = prefersReducedMotion ? 0 : UX_MOTION.panelMs;
    const transition = `${motionMs(transitionDuration)} ${UX_MOTION.easingStandard}`;
    const preloadNavigationTarget = (event: React.SyntheticEvent<HTMLElement>) => {
        const anchor = (event.target as HTMLElement).closest("a[href]");
        if (!(anchor instanceof HTMLAnchorElement) || anchor.origin !== window.location.origin) return;
        preloadRouteForHref(anchor.pathname);
    };
    React.useEffect(() => {
        preloadRouteForHref(location.pathname);
    }, [location.pathname]);
    const marginLeft = isDesktop ? DESKTOP_DRAWER_WIDTH : 0;
    const {
        isOpen: isSecondaryOpen,
        tab: secondaryTab,
        width: secondaryPanelWidth,
        setIsOpen: setIsSecondaryOpen,
        setTab: setSecondaryTab,
        toggleOpen: toggleSecondaryOpen,
    } = useSecondaryPanelPreference();

    const panelTooltip = useMemo(() => {
        if (!isSecondaryOpen) return t("split.openPanel");
        const tabLabels: Record<string, string> = {
            quotes: t("navigation.cotizaciones"),
            wallet: t("navigation.wallet"),
            portfolio: t("navigation.portfolio"),
        };
        return t("split.closePanelWithTab", { tab: tabLabels[secondaryTab] ?? secondaryTab });
    }, [isSecondaryOpen, secondaryTab, t]);

    return (
        <MarketQuotesProvider>
            <div onFocusCapture={preloadNavigationTarget} onMouseOverCapture={preloadNavigationTarget}>
                <CssBaseline />

                <OfflineBanner />

                {/* Navigation shell (permanent Drawer on desktop, AppBar+Drawer on mobile) */}
                <ResponsiveNavbar />

                {/* Main content area */}
                <Box
                    component="main"
                    data-onboarding-id="main-content"
                    sx={{
                        flexGrow: 1,
                        minHeight: "100vh",
                        // Offset for the permanent Drawer on desktop
                        ml: `${marginLeft}px`,
                        pt: { xs: 0, md: 2 },
                        px: { xs: 0, sm: 3, md: 4 },
                        pb: { xs: MOBILE_CONTENT_BOTTOM_OFFSET, md: 8 },
                    }}
                >
                    {isDesktop ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: isVerticalDesktop ? "column" : "row",
                                alignItems: "stretch",
                                gap: 2,
                                minHeight: DESKTOP_CONTENT_HEIGHT,
                                height: isVerticalDesktop && isSecondaryOpen ? DESKTOP_CONTENT_HEIGHT : undefined,
                            }}
                        >
                            <Box
                                sx={{
                                    flex: isVerticalDesktop && isSecondaryOpen ? "1 1 0" : 1,
                                    minWidth: 0,
                                    minHeight: 0,
                                    overflowY: isVerticalDesktop && isSecondaryOpen ? "auto" : undefined,
                                    transition: `all ${transition}, flex ${transition}`,
                                }}
                            >
                                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                                    <Tooltip title={panelTooltip}>
                                        <Button
                                            size="small"
                                            variant={isSecondaryOpen ? "outlined" : "text"}
                                            startIcon={<ViewSidebarIcon />}
                                            onClick={toggleSecondaryOpen}
                                            aria-expanded={isSecondaryOpen}
                                            sx={{ minHeight: 40 }}
                                        >
                                            {isSecondaryOpen ? t("split.closePanel") : t("split.openPanel")}
                                        </Button>
                                    </Tooltip>
                                </Box>
                                <Outlet />
                                <Footer />
                            </Box>

                            <Box
                                sx={{
                                    width: isVerticalDesktop ? "100%" : (isSecondaryOpen ? secondaryPanelWidth : "0px"),
                                    height: isVerticalDesktop ? (isSecondaryOpen ? "calc(50% - 8px)" : "0px") : "auto",
                                    opacity: isSecondaryOpen ? 1 : 0,
                                    transform: isSecondaryOpen
                                        ? "translate(0, 0)"
                                        : (isVerticalDesktop ? "translateY(16px)" : "translateX(16px)"),
                                    transition: `width ${transition}, height ${transition}, opacity ${motionMs(prefersReducedMotion ? 0 : UX_MOTION.standardMs)} ${UX_MOTION.easingStandard}, transform ${transition}`,
                                    overflow: "hidden",
                                    pointerEvents: isSecondaryOpen ? "auto" : "none",
                                    flexShrink: 0,
                                }}
                            >
                                {isSecondaryOpen && (
                                    <DesktopSecondaryPanel
                                        tab={secondaryTab}
                                        onTabChange={setSecondaryTab}
                                        onClose={() => setIsSecondaryOpen(false)}
                                        layout={isVerticalDesktop ? "stacked" : "side"}
                                    />
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <>
                            <Outlet />
                            <Footer />
                        </>
                    )}
                </Box>

                <Toaster richColors position="top-right" duration={3000} />
                <FirstUseOnboarding />
            </div>
        </MarketQuotesProvider>
    );
}

export default MainLayout;
